import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Text, VStack, HStack, Select, Spinner,
  Box, Badge, Input, Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon,
  Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid, Radio, RadioGroup, Stack,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Divider,
  Checkbox, CheckboxGroup, Collapse
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { getBackendURL } from "../../components/env";
import { supabasePublic } from "../../components/supabaseClient";

const DEFAULT_BUCKET = "backup-audio";
const CONFIRM_WORD = "DELETE";

// The storage endpoints require a signed-in admin/superadmin/doctor (backend `_guard`).
async function authHeaders(extra = {}) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again");
  return { ...extra, Authorization: `Bearer ${token}` };
}

export default function StorageModal({ isOpen, onClose }) {
  const [view, setView] = useState("menu");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [buckets, setBuckets] = useState([]);
  // Audio buckets cleanup may run on; every other bucket is audit-only
  const [cleanupBuckets, setCleanupBuckets] = useState([DEFAULT_BUCKET]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [selectedBuckets, setSelectedBuckets] = useState([DEFAULT_BUCKET]);
  // One audit report per checked bucket; the report screen shows them all together
  const [reports, setReports] = useState({});
  // Bucket targeted by "Specific Duplicates by Date" (the only single-bucket strategy)
  const [drillBucket, setDrillBucket] = useState("");
  const [progress, setProgress] = useState("");
  const [operationResults, setOperationResults] = useState([]);

  const [operationMode, setOperationMode] = useState("all_duplicates");
  const [confirmText, setConfirmText] = useState("");

  // Option params
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTopFolder, setSelectedTopFolder] = useState("");
  const [selectedDateFolder, setSelectedDateFolder] = useState("");
  const [autoRelocate, setAutoRelocate] = useState(false);
  const [showRelocateInfo, setShowRelocateInfo] = useState(false);

  const backendUrl = getBackendURL();

  useEffect(() => {
    if (isOpen) {
      setView("menu");
      setErrorMsg("");
      setReports({});
      setDrillBucket("");
      setSelectedBuckets([DEFAULT_BUCKET]);
      setOperationResults([]);
      setConfirmText("");
      setOperationMode("all_duplicates");
      setStartDate("");
      setEndDate("");
      setAutoRelocate(false);
      setShowRelocateInfo(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setBucketsLoading(true);
    authHeaders()
      .then((headers) => fetch(`${backendUrl}/api/storage/buckets/`, { headers }))
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (!res.ok || data.error) throw new Error(data.error || "Failed to list buckets.");
        if (!cancelled) {
          setBuckets(data.buckets || []);
          setCleanupBuckets(data.cleanup_buckets || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBuckets([DEFAULT_BUCKET]);
          setCleanupBuckets([DEFAULT_BUCKET]);
          setErrorMsg(`Could not load bucket list: ${err.message}`);
        }
      })
      .finally(() => {
        if (!cancelled) setBucketsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, backendUrl]);

  const handleDrillBucketChange = (bucket, data = reports[bucket]) => {
    setDrillBucket(bucket);
    setSelectedTopFolder(data?.drill_down_tree?.[0]?.name || "");
    setSelectedDateFolder("");
  };

  const handleGenerateReport = async () => {
    // Audit in bucket-list order, one at a time — each audit walks the whole bucket
    const toAudit = buckets.filter((b) => selectedBuckets.includes(b));
    if (toAudit.length === 0) return;

    setLoading(true);
    setErrorMsg("");
    const collected = {};
    const failures = [];
    for (let i = 0; i < toAudit.length; i++) {
      const bucket = toAudit[i];
      setProgress(`Auditing ${toAudit.length > 1 ? `${bucket} (${i + 1} of ${toAudit.length})` : bucket}...`);
      try {
        const res = await fetch(`${backendUrl}/api/storage/report/`, {
          method: "POST",
          headers: await authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ bucket }),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || "Failed to scan storage.");
        collected[bucket] = data;
      } catch (err) {
        failures.push(`${bucket}: ${err.message}`);
      }
    }
    setProgress("");
    setLoading(false);

    if (failures.length) setErrorMsg(`Audit failed for ${failures.join("; ")}`);
    const audited = Object.keys(collected);
    if (audited.length === 0) return;
    setReports(collected);
    setOperationMode("all_duplicates");
    setConfirmText("");
    const firstCleanable = audited.find((b) => collected[b].cleanup_supported);
    if (firstCleanable) handleDrillBucketChange(firstCleanable, collected[firstCleanable]);
    else handleDrillBucketChange("", null);
    setView("report");
  };

  const handleExecuteOperation = async () => {
    // Drill-down targets one bucket; every other strategy runs on each audited audio bucket
    // in turn. Audit-only (non-audio) buckets are never sent to cleanup.
    const targets = operationMode === "folder_drill" ? [drillBucket] : cleanableBuckets;
    setLoading(true);
    setErrorMsg("");
    const results = [];
    const failures = [];
    for (let i = 0; i < targets.length; i++) {
      const bucket = targets[i];
      setProgress(`Cleaning ${targets.length > 1 ? `${bucket} (${i + 1} of ${targets.length})` : bucket}...`);
      try {
        const payload = {
          bucket,
          mode: operationMode,
          confirm_text: confirmText,
          start_date: startDate,
          end_date: endDate,
          files: operationMode === "folder_drill" ? currentFiles.map((f) => f.name) : [],
          // When true, the backend runs move_to_save + reorganize_dated (all files) after the cleanup
          auto_relocate: autoRelocate,
        };
        const res = await fetch(`${backendUrl}/api/storage/cleanup/`, {
          method: "POST",
          headers: await authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (!res.ok || result.error) throw new Error(result.error || "Operation failed.");
        results.push(result);
      } catch (err) {
        failures.push(`${bucket}: ${err.message}`);
      }
    }
    setProgress("");
    setLoading(false);

    if (failures.length) setErrorMsg(`Cleanup failed for ${failures.join("; ")}`);
    if (results.length === 0) return;
    setOperationResults(results);
    setView("results");
  };

  const auditedBuckets = Object.keys(reports);
  const multiBucket = auditedBuckets.length > 1;
  // Audited audio buckets — the only ones cleanup/relocation touch
  const cleanableBuckets = auditedBuckets.filter((b) => reports[b].cleanup_supported);
  const auditOnlyBuckets = auditedBuckets.filter((b) => !reports[b].cleanup_supported);
  const multiCleanable = cleanableBuckets.length > 1;
  const sumPreview = (key, list) => list.reduce((n, b) => n + reports[b].target_bucket_preview[key], 0);
  const totals = {
    total_files: sumPreview("total_files", auditedBuckets),
    files_to_keep: sumPreview("files_to_keep", cleanableBuckets),
    duplicates_to_delete: sumPreview("duplicates_to_delete", cleanableBuckets),
  };
  // Duplicate folders from every audio bucket, prefixed with the bucket name when there are several
  const dupGroups = cleanableBuckets.flatMap((b) =>
    Object.entries(reports[b].duplicates_by_folder || {}).map(([top, dates]) => ({
      key: `${b}/${top}`,
      label: multiBucket ? `${b} / ${top}` : top,
      dates,
    }))
  );
  const audioBucketOptions = buckets.filter((b) => cleanupBuckets.includes(b));
  const otherBucketOptions = buckets.filter((b) => !cleanupBuckets.includes(b));
  const drillData = reports[drillBucket];
  const currentTop = drillData?.drill_down_tree?.find((t) => t.name === selectedTopFolder);
  // The backend only sends date folders that contain at least one duplicate
  const availableDates = currentTop ? Object.keys(currentTop.dates) : [];
  const currentFiles = (currentTop && selectedDateFolder) ? (currentTop.dates[selectedDateFolder] || []) : [];
  const drillDownDuplicates = currentFiles.filter((f) => f.is_duplicate);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader pb={1}>
          {view === "menu" && "Storage & Media Operations"}
          {view === "report" && "Storage Report & Execution Pipeline"}
          {view === "results" && "Operation Summary"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          {errorMsg && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {errorMsg}
            </Alert>
          )}

          {loading && (
            <VStack py={8} spacing={3} align="center">
              <Spinner size="xl" thickness="3px" color="teal.500" />
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {progress}
              </Text>
            </VStack>
          )}

          {/* VIEW: Menu */}
          {!loading && view === "menu" && (
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Audit Supabase storage buckets and clean duplicate audio chunks. Optionally can relocate audio files to dated folders.
              </Text>

              <Box>
                <Text fontWeight="semibold" mb={1}>Buckets to audit:</Text>
                {bucketsLoading ? (
                  <HStack spacing={2} py={1}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.600">Loading buckets...</Text>
                  </HStack>
                ) : (
                  <CheckboxGroup
                    colorScheme="teal"
                    value={selectedBuckets}
                    onChange={(vals) => setSelectedBuckets(vals)}
                  >
                    <Stack spacing={1} borderWidth="1px" borderRadius="md" p={3} maxW="400px">
                      {audioBucketOptions.length > 0 && (
                        <Badge colorScheme="teal" alignSelf="flex-start" mb={1}>Audio buckets</Badge>
                      )}
                      {audioBucketOptions.map((b) => (
                        <Checkbox key={b} value={b}>
                          <Text fontSize="sm">{b}</Text>
                        </Checkbox>
                      ))}
                      {otherBucketOptions.length > 0 && (
                        <Box pt={audioBucketOptions.length > 0 ? 2 : 0}>
                          <Badge colorScheme="gray" mb={1}>Non-audio buckets</Badge>
                          <Text fontSize="xs" color="gray.500" mb={1}>
                            Audit only &mdash; cleanup and relocation never run on these.
                          </Text>
                        </Box>
                      )}
                      {otherBucketOptions.map((b) => (
                        <Checkbox key={b} value={b}>
                          <Text fontSize="sm">{b}</Text>
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Each bucket is audited in turn; large buckets can take a while.
                </Text>
              </Box>

              <Button
                colorScheme="teal"
                alignSelf="flex-start"
                onClick={handleGenerateReport}
                isDisabled={bucketsLoading || !buckets.some((b) => selectedBuckets.includes(b))}
              >
                Audit & Generate Action Plan
              </Button>
            </VStack>
          )}

          {/* VIEW: Report & Integrated Operations Flow */}
          {!loading && view === "report" && auditedBuckets.length > 0 && (
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={3} spacing={3}>
                <Stat p={3} borderWidth="1px" borderRadius="md">
                  <StatLabel fontSize="xs">Total Files</StatLabel>
                  <StatNumber fontSize="xl">{totals.total_files}</StatNumber>
                  <StatHelpText m={0}>
                    {multiBucket ? `Across ${auditedBuckets.length} buckets` : `Bucket: ${auditedBuckets[0]}`}
                  </StatHelpText>
                </Stat>
                <Stat p={3} borderWidth="1px" borderRadius="md">
                  <StatLabel fontSize="xs">Retained Keepers</StatLabel>
                  <StatNumber fontSize="xl" color="blue.600">{totals.files_to_keep}</StatNumber>
                  <StatHelpText m={0}>
                    {auditOnlyBuckets.length ? "Audio buckets only" : "Largest / Newest copies"}
                  </StatHelpText>
                </Stat>
                <Stat p={3} borderWidth="1px" borderRadius="md">
                  <StatLabel fontSize="xs">Marked Duplicates</StatLabel>
                  <StatNumber fontSize="xl" color="red.500">{totals.duplicates_to_delete}</StatNumber>
                  <StatHelpText m={0}>
                    {cleanableBuckets.length === 0
                      ? "No audio bucket audited"
                      : multiBucket
                        ? "See per-bucket reclaimable size below"
                        : `~${reports[auditedBuckets[0]].target_bucket_preview.readable_reclaimable_size} reclaimable`}
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Bucket Overview */}
              <Box>
                <Text fontWeight="bold" fontSize="xs" mb={1}>Audited Buckets Summary</Text>
                <Table size="sm" variant="simple" borderWidth="1px">
                  <Thead>
                    <Tr>
                      <Th>Bucket</Th>
                      <Th isNumeric>Files</Th>
                      <Th isNumeric>Folders</Th>
                      <Th isNumeric>Size</Th>
                      <Th isNumeric>Duplicates</Th>
                      <Th isNumeric>Reclaimable</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {auditedBuckets.map((b) => {
                      const p = reports[b].target_bucket_preview;
                      return (
                        <Tr key={b}>
                          <Td fontWeight="bold">
                            {b}
                            {!reports[b].cleanup_supported && (
                              <Badge ml={2} colorScheme="gray" fontSize="9px">Audit only</Badge>
                            )}
                          </Td>
                          <Td isNumeric>{p.total_files}</Td>
                          <Td isNumeric>{p.folders_count}</Td>
                          <Td isNumeric>{p.readable_total_size}</Td>
                          <Td isNumeric>{reports[b].cleanup_supported ? p.duplicates_to_delete : "—"}</Td>
                          <Td isNumeric>{reports[b].cleanup_supported ? p.readable_reclaimable_size : "—"}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              {/* Folder-Organized Duplicates Accordion Dropdown */}
              <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold" fontSize="xs">
                    Identified Duplicate Files ({totals.duplicates_to_delete})
                  </Text>
                  <Badge colorScheme="red">Candidate Removals</Badge>
                </HStack>

                <Box maxH="180px" overflowY="auto" bg="white" borderRadius="md" borderWidth="1px" p={1}>
                  {dupGroups.length === 0 ? (
                    <Text fontSize="xs" color="gray.500" p={2}>
                      {cleanableBuckets.length === 0
                        ? "No audio bucket audited — duplicate detection only runs on audio buckets."
                        : `No duplicate files found in ${multiCleanable ? "the audited audio buckets" : "this bucket"}.`}
                    </Text>
                  ) : (
                    <Accordion allowMultiple size="sm">
                      {dupGroups.map(({ key, label, dates }) => (
                        <AccordionItem key={key} borderBottomWidth="1px">
                          <AccordionButton py={1.5}>
                            <Box as="span" flex="1" textAlign="left" fontWeight="bold" fontSize="xs">
                              📁 {label}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          {/* unmountOnExit: collapsed panels would otherwise still render every duplicate row */}
                          <AccordionPanel pb={2} pt={1} pl={4} motionProps={{ unmountOnExit: true }}>
                            {Object.keys(dates).map((dKey) => (
                              <Box key={dKey} mb={2}>
                                <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                                  📅 {dKey} ({dates[dKey].length})
                                </Text>
                                <VStack align="stretch" spacing={0.5} pl={2} borderLeftWidth="2px" borderColor="gray.200">
                                  {dates[dKey].map((file) => (
                                    <HStack key={file.name} justify="space-between" fontSize="xs" py={0.5}>
                                      <Text noOfLines={1} color="red.700">• {file.base_name}</Text>
                                      <Text fontSize="10px" color="gray.500">{(file.size / 1024).toFixed(1)} KB</Text>
                                    </HStack>
                                  ))}
                                </VStack>
                              </Box>
                            ))}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </Box>
              </Box>

              {auditOnlyBuckets.length > 0 && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {cleanableBuckets.length > 0
                    ? `Execute Cleanup won't run on ${auditOnlyBuckets.join(", ")} — ${auditOnlyBuckets.length > 1 ? "they aren't audio buckets" : "it isn't an audio bucket"}, so ${auditOnlyBuckets.length > 1 ? "they're" : "it's"} audit only. Cleanup and relocation run on ${cleanableBuckets.join(", ")}.`
                    : "None of the audited buckets are audio buckets, so there is nothing to clean up. Non-audio buckets are audit only."}
                </Alert>
              )}

              {cleanableBuckets.length > 0 && (<>
              <Divider />

              {/* Single Flow Strategy Selector */}
              <Box borderWidth="1px" p={4} borderRadius="md" bg="gray.50">
                <Text fontWeight="bold" fontSize="sm" mb={2}>Select Operation Strategy:</Text>
                <RadioGroup value={operationMode} onChange={(val) => { setOperationMode(val); setConfirmText(""); }}>
                  <Stack spacing={2}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Cleanup & Deduplication</Text>
                    <Radio value="all_duplicates">
                      Delete ALL duplicates across {multiCleanable ? "all audited audio buckets" : "bucket"} ({totals.duplicates_to_delete} files)
                    </Radio>
                    <Radio value="date_range">
                      Filter duplicates by Date Range
                    </Radio>
                    <Radio value="folder_drill">
                      Specific Duplicates by Date
                    </Radio>
                  </Stack>
                </RadioGroup>

                {/* Sub-configs */}
                {operationMode === "date_range" && (
                  <VStack align="stretch" spacing={2} mt={3} p={3} bg="white" borderRadius="md" borderWidth="1px">
                    <HStack>
                      <Box flex="1">
                        <Text fontSize="xs" fontWeight="semibold">Start Date:</Text>
                        <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                      </Box>
                      <Box flex="1">
                        <Text fontSize="xs" fontWeight="semibold">End Date:</Text>
                        <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                      </Box>
                    </HStack>
                  </VStack>
                )}

                {operationMode === "folder_drill" && (
                  <VStack align="stretch" spacing={3} mt={3} p={3} bg="white" borderRadius="md" borderWidth="1px">
                    <HStack>
                      {multiCleanable && (
                        <Box flex="1">
                          <Text fontSize="xs" fontWeight="semibold">Bucket:</Text>
                          <Select size="sm" value={drillBucket} onChange={(e) => handleDrillBucketChange(e.target.value)}>
                            {cleanableBuckets.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </Select>
                        </Box>
                      )}
                      <Box flex="1">
                        <Text fontSize="xs" fontWeight="semibold">Storage Folder:</Text>
                        <Select size="sm" value={selectedTopFolder} onChange={(e) => { setSelectedTopFolder(e.target.value); setSelectedDateFolder(""); }}>
                          {drillData?.drill_down_tree?.map((top) => (
                            <option key={top.name} value={top.name}>{top.name} ({top.duplicates_count} dups)</option>
                          ))}
                        </Select>
                      </Box>
                      <Box flex="1">
                        <Text fontSize="xs" fontWeight="semibold">Date Folder:</Text>
                        <Select size="sm" value={selectedDateFolder} onChange={(e) => setSelectedDateFolder(e.target.value)}>
                          <option value="">-- Choose Date --</option>
                          {availableDates.map((d) => <option key={d} value={d}>{d}</option>)}
                        </Select>
                      </Box>
                    </HStack>

                    {selectedDateFolder && (
                      <Box maxH="120px" overflowY="auto" borderWidth="1px" p={2} borderRadius="md" bg="gray.50">
                        {currentFiles.map((f) => (
                          <HStack key={f.name} justify="space-between" fontSize="xs" py={0.5}>
                            <Text noOfLines={1}>
                              <Badge colorScheme={f.is_duplicate ? "red" : "green"} mr={2}>
                                {f.is_duplicate ? "DUPLICATE" : "KEEP"}
                              </Badge>
                              {f.base_name}
                            </Text>
                            <Text color="gray.500">{(f.size / 1024).toFixed(1)} KB</Text>
                          </HStack>
                        ))}
                      </Box>
                    )}
                  </VStack>
                )}

              </Box>

              {/* Optional post-cleanup relocation */}
              <Box borderWidth="1px" borderRadius="md" p={3} bg="orange.50" borderColor="orange.200">
                <HStack justify="space-between" align="center">
                  <Checkbox
                    colorScheme="orange"
                    isChecked={autoRelocate}
                    onChange={(e) => setAutoRelocate(e.target.checked)}
                  >
                    <Text fontSize="sm" fontWeight="bold" color="orange.800">
                      Relocate files after cleanup
                    </Text>
                  </Checkbox>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="orange"
                    rightIcon={showRelocateInfo ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setShowRelocateInfo((v) => !v)}
                  >
                    {showRelocateInfo ? "Hide details" : "What does this do?"}
                  </Button>
                </HStack>
                <Collapse in={showRelocateInfo} animateOpacity>
                  <VStack align="stretch" spacing={1} mt={2} fontSize="xs" color="orange.900">
                    <Text>
                      Recordings are uploaded to the root of the bucket, which becomes one huge flat
                      folder that is slow to list and hard to browse or clean up. Relocation files
                      everything into dated folders in two steps:
                    </Text>
                    <Text>
                      1. Every file at the bucket root is moved into <code>SAVE/</code> (a staging folder).
                    </Text>
                    <Text>
                      2. Every file in <code>SAVE/</code> is moved to <code>public/YYYY/YYYY-MM-DD/</code>,
                      using the date in its file name (or its upload date if the name has none).
                    </Text>
                    <Text>
                      Files are only moved, never deleted or renamed, and it runs on the whole bucket
                      (not just the files selected above). It is separate from duplicate removal and runs
                      whenever this box is ticked, even if there are no duplicates to delete.
                    </Text>
                  </VStack>
                </Collapse>
                {autoRelocate ? (
                  <VStack align="stretch" spacing={2} mt={2}>
                    {(operationMode === "folder_drill" ? [drillBucket] : cleanableBuckets).map((b) => {
                      const rel = reports[b].relocation_preview;
                      return (
                        <Box key={b}>
                          {multiCleanable && (
                            <Text fontSize="xs" fontWeight="bold" color="orange.800">{b}</Text>
                          )}
                          <Text fontSize="xs" color="orange.800">
                            1. Bucket Root &rarr; <code>SAVE/</code> ({rel.root_files_count} files, {rel.root_files_size})
                          </Text>
                          <Text fontSize="xs" color="orange.800">
                            2. <code>SAVE/</code> &rarr; <code>public/YYYY/YYYY-MM-DD/</code> ({rel.save_files_count} files, {rel.save_files_size})
                          </Text>
                        </Box>
                      );
                    })}
                  </VStack>
                ) : (
                  <Text fontSize="xs" color="orange.800" mt={2}>
                    Relocation skipped &mdash; only the cleanup will run.
                  </Text>
                )}
              </Box>

              {/* Typed Confirmation Box */}
              <Box bg="red.50" p={3} borderRadius="md" borderWidth="1px" borderColor="red.200">
                <Text fontSize="xs" fontWeight="bold" color="red.800" mb={1}>
                  To proceed, type {CONFIRM_WORD} below:
                </Text>
                <Input
                  size="sm"
                  bg="white"
                  placeholder={CONFIRM_WORD}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </Box>
              </>)}
            </VStack>
          )}

          {/* VIEW: Results */}
          {!loading && view === "results" && operationResults.length > 0 && (
            <VStack spacing={4} align="stretch">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Operation successfully completed!
              </Alert>
              {operationResults.map((result) => (
                <Box key={result.bucket} borderWidth="1px" borderRadius="md" p={4}>
                  <Text fontSize="sm"><strong>Bucket:</strong> {result.bucket}</Text>
                  <Text fontSize="sm"><strong>Files Deleted:</strong> {result.deleted_count}</Text>
                  {result.relocation && (
                    <Box mt={3}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>File Relocation</Text>
                      <VStack align="stretch" spacing={1}>
                        {result.relocation.map((step) => (
                          <HStack key={step.action} justify="space-between" fontSize="sm">
                            <Text>
                              {step.action === "move_to_save"
                                ? <>Bucket Root &rarr; <code>SAVE/</code></>
                                : <><code>SAVE/</code> &rarr; <code>public/YYYY/YYYY-MM-DD/</code></>}
                            </Text>
                            <HStack spacing={2}>
                              <Badge colorScheme="green">{step.moved_count} moved</Badge>
                              {step.failures_count > 0 && <Badge colorScheme="red">{step.failures_count} failed</Badge>}
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {view === "report" && (
            <>
              <Button variant="ghost" mr="auto" onClick={() => setView("menu")}>
                Back to Menu
              </Button>
              {cleanableBuckets.length > 0 && (
              <Button
                colorScheme="red"
                onClick={handleExecuteOperation}
                isLoading={loading}
                isDisabled={
                  confirmText.trim() !== CONFIRM_WORD ||
                  // Relocation can run on its own, so an empty drill-down only blocks a delete-only run
                  (operationMode === "folder_drill" && drillDownDuplicates.length === 0 && !autoRelocate)
                }
              >
                Execute Cleanup
              </Button>
              )}
            </>
          )}

          {view === "results" && (
            <Button colorScheme="teal" onClick={onClose}>
              Done
            </Button>
          )}

          {view === "menu" && (
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
